import * as Sentry from '@sentry/react';
import { ApiError } from './api/errors.js';

/**
 * Browser-side error tracking.
 *
 * The DSN is inlined at build time, so an unset `VITE_SENTRY_DSN` produces a
 * bundle with reporting disabled rather than one that fails at runtime.
 */
let initialised = false;

export function isMonitoringEnabled(): boolean {
  return initialised;
}

export function initMonitoring(): boolean {
  const dsn = import.meta.env['VITE_SENTRY_DSN'];
  if (!dsn || initialised) return initialised;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    // The browser sees real customer data; nothing is collected implicitly.
    sendDefaultPii: false,
    beforeSend: (event) => scrubBrowserEvent(event),
  });

  initialised = true;
  return true;
}

/** Test seam: lets a spec reset the module between cases. */
export function resetMonitoringForTests(): void {
  initialised = false;
}

const SENSITIVE_KEY = /(password|token|secret|authorization|cookie|apikey)/i;

export function scrubBrowserEvent<T extends Sentry.ErrorEvent>(event: T): T {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
  }

  if (event.extra) {
    event.extra = Object.fromEntries(
      Object.entries(event.extra).map(([key, value]) =>
        SENSITIVE_KEY.test(key) ? [key, '[REDACTED]'] : [key, value],
      ),
    ) as typeof event.extra;
  }

  return event;
}

/**
 * Reports a client-side failure.
 *
 * A 4xx is the server correctly rejecting something and is not reported; a 5xx
 * is, tagged with the same requestId the backend logged, so the two halves of
 * one failure line up.
 */
export function captureError(error: unknown, context: Record<string, string> = {}): void {
  if (error instanceof ApiError) {
    if (error.status < 500) return;

    Sentry.withScope((scope) => {
      scope.setTag('errorCode', error.code);
      scope.setTag('statusCode', String(error.status));
      for (const [key, value] of Object.entries(context)) scope.setTag(key, value);
      Sentry.captureException(error);
    });
    return;
  }

  if (!initialised) return;

  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(context)) scope.setTag(key, value);
    Sentry.captureException(error);
  });
}

export function identifyUser(user: { id: string } | null): void {
  if (!initialised) return;
  // Only the opaque ID. Never contact information or permissions.
  Sentry.setUser(user ? { id: user.id } : null);
}
